// Single source of truth for the Redux migration of WorkStateContext + StateContext.
//
// Every field below was transcribed verbatim (name + initial value) from the original
// useState() declarations in src/context/WorkStateContext.js and src/context/StateContext.js,
// grouped into domain slices per REDUX_MIGRATION_PLAN.md §5.
//
// NOTE: `classObj` from the original WorkStateContext is intentionally NOT here. It holds a
// `selectionMode` CLASS INSTANCE (see script/SelectionMode.js) whose `.updateSelectedArray()`
// method is called in MasonryGrid.js — a non-serializable value that must not live in Redux.
// It is kept in a plain module (store/workRefs.js) via getClassObj()/setClassObj(), read at
// call-time so consumers never capture a stale reference.

// Slice name -> { field: initialValue }.  Initial values copied verbatim from the source.
export const SLICE_DEFS = {
  data: {
    data: undefined,
    collection: null,
    collectionData: undefined,
    collectionFormattedData: {},
    images: undefined,
    galleryImages: undefined,
    loadedImages: {},
    filteredData: false,
    recallCollection: false,
  },
  video: {
    bookingPK: undefined,
    videoUrl: undefined,
    videoDetails: undefined,
    currentQuality: 1080,
    currentQualityUrl: undefined,
    qualityVideo: false,
    hdUrl: false,
    loadingHD: false,
  },
  ui: {
    scrollPosition: 0,
    prevScrollPosition: 0,
    photoScroll: 0,
    distanceFromTop: 0,
    height: window.innerHeight,
    currentDimensions: window?.innerWidth,
    fullScreen: false,
    showTooltip: false,
    showArrow: false,
    isScrollable: false,
    contentLoading: false,
    albumView: false,
    videoView: false,
    showGallery: false,
    showFilters: true,
    showMenuSidebar: false,
    showToast: false,
    toastMessage: undefined,
    loading: false,
    updateLoader: false,
    workIdError: false,
    workIdErrorMessage: 'Something went wrong.',
    tncState: false,
    nickname: '',
    redirectUrl: [],
    pillMargin: 15,
    pastMargin: 15,
    homeUrl: undefined,
  },
  selection: {
    selection: false,
    selectionType: undefined,
    selectedImages: {},
    selectionAll: {},
    selectImages: {},
    hideSelection: false,
    downloadSelection: false,
    deleteSelection: false,
    approveFlagSelection: false,
    addToSelection: false,
    printSelection: false,
    multipleSelect: [],
    updateSinglePhoto: false,
    printSelectedData: {},
  },
  gallery: {
    photoIndex: undefined,
    indexDisplay: 1,
    gridFirstCheck: true,
    initialCarousel: true,
    hideSelectedObj: {},
    croppedImages: {},
    shareFaceFilter: true,
  },
  filter: {
    filterDropdown: false,
    selectedOption: 'showall',
    filterWorkId: [],
    allGuestPhotos: true,
    yourUploads: undefined,
    nextCollection: false,
    nextCollectionName: null,
    nextVideo: null,
    prevCollectionName: null,
    prevCollection: null,
    galleryNextCollectionPopup: false,
    galleryCollectionName: null,
    galleryRightArrow: true,
  },
  faceSearch: {
    faceFilter: false,
    faceSearchState: false,
    faceCollection: undefined,
    faceSearchData: undefined,
    faceSearchOn: false,
    pastFaceSearches: undefined,
    faceFormattedData: {},
    faceOnly: false,
    faceSelect: {},
    faceHide: {},
    readyFaceSearchDownload: false,
    pastFaceSearchesOn: false,
    selfieImage: undefined,
  },
  album: {
    albumData: undefined,
    defAlbumData: undefined,
    singleAlbumData: undefined,
    albumTitle: undefined,
    albumLocked: 0,
    isEditing: false,
    albumBookingPopup: false,
    albumBookingData: undefined,
  },
  share: {
    shareDropdown: false,
    shareLinkData: undefined,
    shareLinkPopup: false,
    shareSelective: false,
    shareSelectList: {},
    shareTab: 'photos',
  },
  modal: {
    isPopupOpen: false,
    moreDropdown: false,
    lockPopup: false,
    likeViewPopup: false,
    newFolderPopup: false,
    flagPhotoPopup: false,
    downloadPopup: false,
    folderDeletePopup: false,
    trashDeletePopup: null,
    allTrashDeletePopup: false,
    uploadExpiryOpen: false,
    isAddToOpen: false,
    isSingleAddToOpen: false,
    downloadDetails: undefined,
    reloadDelete: false,
    deleteConfirm: false,
    deletePopup: false,
    deleteType: false,
    expiryDate: '',
  },
  folder: {
    userFolderData: undefined,
    folderName: '',
    checkedFolder: [],
    libraryData: undefined,
    hiddenWork: {},
    likeData: undefined,
    currentPhotoLiked: false,
    currentPhotoFlagged: false,
    flaggedData: undefined,
    floatingBars: [],
    trashFoldersInfo: null,
  },
  // --- from StateContext ---
  session: {
    isOpen: false,
    userDetails: null,
    isLoggedin: false,
  },
  analytics: {
    posthogEvents: {},
    pages: {},
    phTrack: {},
    buttonEventMap: {},
    scrollEventMap: {},
  },
  // --- app-lifetime, not from either context ---
  // `blocked` is the api-failure-guard flag: App.js sets it when requests start failing and renders
  // the CustomStatus overlay until the cooldown ends. It is deliberately NOT in a gallery slice —
  // its lifetime is the whole app, so the WorkDetail reset must not be able to clear it.
  apiGuard: {
    blocked: false,
  },
};

// Slices are split by LIFETIME, because that is what the WorkDetail reset keys off.
//
// WORKSTATE_SLICES are scoped to one visit to the gallery: they are wiped when the WorkDetail
// subtree mounts and unmounts, reproducing the old nested WorkStateProvider.
// APP_LIFETIME_SLICES outlive any route change and are never reset (`session` + `analytics` came
// from StateContext, which was mounted once at the app root; `apiGuard` is app-owned too).
export const WORKSTATE_SLICES = ['data', 'video', 'ui', 'selection', 'gallery', 'filter', 'faceSearch', 'album', 'share', 'modal', 'folder'];
export const APP_LIFETIME_SLICES = ['session', 'analytics', 'apiGuard'];

// Setter names that do NOT follow `set` + Capitalized(field). Verified against source.
export const SETTER_OVERRIDES = {
  expiryDate: 'setexpiryDate',
  isOpen: 'setisOpen',
  userDetails: 'setuserDetails',
  isLoggedin: 'setisLoggedin',
};

export function setterName(field) {
  return SETTER_OVERRIDES[field] || 'set' + field.charAt(0).toUpperCase() + field.slice(1);
}

// field -> slice lookup, derived from SLICE_DEFS.
export const FIELD_TO_SLICE = Object.entries(SLICE_DEFS).reduce((acc, [slice, fields]) => {
  Object.keys(fields).forEach((f) => { acc[f] = slice; });
  return acc;
}, {});
